import React, {RefObject} from "react";
import {connect} from "react-redux";
import {
    DisplayType, EditorType, formatString, Nullable,
} from "@data-force/app";
import {Modal, Spin} from "antd";
import {LoadingOutlined, CloseCircleOutlined} from "@ant-design/icons";
import {SpinIndicator} from "antd/lib/spin";
import {getTranslate, TranslateFunction} from "react-localize-redux";
import {ModalFunc} from "antd/lib/modal/confirm";
import CommonView from "../CommonView/CommonView";
import "./MainPage.scss";
import StoreState from "../../interfaces/store/storeState";
import {ContentType} from "../../interfaces/store/actions/payload/layout";
import Dictation from "../../components/Dictation/Dictation";
import Tasks from "../../components/TasksView/TasksView";
import Instructions from "../../components/Instructions/Instructions";
import HistoryParams from "../../interfaces/historyParams";
import {
    batchUploadsAreValid,
    calculateStatusCount,
    calculateStatusData,
    getHistoryParams,
    getUploadFileList,
    isJobExpired,
    isPhaseExpired,
} from "../../utils/common";
import {
    JobMaskData, JobPhase, SetPhaseDetailsData, StatusData, TaskStatusCount,
} from "../../interfaces/store/actions/payload/job";
import {
    CompleteIntakePayload, CompleteType, Status, StatusStats,
} from "../../interfaces/store/actions/payload/task";
import MessageHolder from "../../components/MessageHolder/MessageHolder";
import {MaskMessage, PhaseAccessStatus} from "../../enums/enums";
import MaskMessageHolder from "../../components/MaskMessageHolder/MaskMessageHolder";
import {SetPhaseDetailsAction} from "../../interfaces/store/actions/job";
import {CompleteIntakeAction} from "../../interfaces/store/actions/task";
import {COMPLETE_EVENT} from "../../utils/constants";
import {setPhaseDetails} from "../../store/actions/job";
import {completeIntake} from "../../store/actions/task";

interface MaskDataInfo {
    messageId: string
    statusClass: string
    taskStatusCount: TaskStatusCount
    completeType: Nullable<CompleteType>
}

interface MaskData {
    maskClass: string
    maskMessage: string
    additionalMaskMessage: string
    ariaRole: string
    taskStatusCount: TaskStatusCount
}

interface State {
    isExpiredDialogShowed: boolean
    intervalId: Nullable<number>
}

interface StateProps {
    contentType: ContentType
    displayType: DisplayType
    jobMaskData: JobMaskData
    statusStats: Nullable<StatusStats>
    rejectedTaskCount: number
    translate: TranslateFunction
    uploadedFiles: number
    numberUploadFiles: number
    editor: Nullable<EditorType>
    numberOfTasks: number
    validBatchUploads: boolean
    accessStatus?: PhaseAccessStatus
    timeLimit?: number
    expireTimestamp?: number
    allowedSubmission?: boolean
    phaseStatus: Status
}

interface DispatchProps {
    setPhaseDetails(payload: Partial<SetPhaseDetailsData>): SetPhaseDetailsAction

    completeIntake(event: CompleteIntakePayload): CompleteIntakeAction
}

type Props = StateProps & DispatchProps;

class MainPage extends React.Component<Props, State> {
    private static readonly translationPath: string = "pages.MainPage";

    private manifoldContent: RefObject<HTMLHeadingElement> = React.createRef();

    private instructionsContent: RefObject<HTMLHeadingElement> = React.createRef();

    private dictationContent: RefObject<HTMLDivElement> = React.createRef();

    state: State = {
        isExpiredDialogShowed: false,
        intervalId: null,
    };

    constructor(props: Props) {
        super(props);

        this.createPageContent = this.createPageContent.bind(this);
        this.createMaskData = this.createMaskData.bind(this);
        this.createSpinIndicator = this.createSpinIndicator.bind(this);
        this.createMaskTipAndAlert = this.createMaskTipAndAlert.bind(this);
        this.focusInstructionsHeader = this.focusInstructionsHeader.bind(this);
        this.focusManifoldContent = this.focusManifoldContent.bind(this);
        this.focusDictationContent = this.focusDictationContent.bind(this);
        this.markPhaseAsExpired = this.markPhaseAsExpired.bind(this);
    }

    componentDidUpdate(): void {
        const {
            timeLimit, expireTimestamp, accessStatus, phaseStatus,
        }: Props = this.props;
        const {intervalId, isExpiredDialogShowed}: State = this.state;
        if (timeLimit && expireTimestamp && !isPhaseExpired(accessStatus) && !intervalId) {
            const expiryInterval: number = window.setInterval(this.markPhaseAsExpired, expireTimestamp - Date.now());
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({intervalId: expiryInterval});
        }

        if (!isExpiredDialogShowed && isJobExpired({phaseStatus, accessStatus})) {
            this.showExpiredJobDialog();
        }
    }

    /**
     * Creates displayable overlay mask data
     *
     * @param {MaskDataInfo} maskDataInfo
     * @return {MaskData}
     */
    createMaskData(maskDataInfo: MaskDataInfo): MaskData {
        const {
            translate, uploadedFiles, numberUploadFiles, rejectedTaskCount,
        }: Props = this.props;
        const {translationPath}: typeof MainPage = MainPage;
        const {messageId, taskStatusCount, completeType}: MaskDataInfo = maskDataInfo;
        const {
            reopened, completed, inProgress, rejected,
        }: TaskStatusCount = taskStatusCount;

        /*
        Side note: the tasks that were selected to be reopened (in the QA phase, e.g.) ought to have status REOPENED before we open the Collection phase.
        However, once we open the Intaker, the status of the first of those tasks (and each following task, once selected) will have the status of IN_PROGRESS.
        Also, 'reopened' task status can come from the Collection phase.
        */
        const intakeCompleted: boolean = (inProgress === 0 && reopened === 0
            && (completed > 0 || rejectedTaskCount > 0));

        let maskClass: string = `df-mask-indicator ${intakeCompleted ? "df-mask-indicator-terminal" : ""}`;
        let maskMessage: string = "";
        let additionalMaskMessage = "";
        let ariaRole: Nullable<string> = null;
        if (messageId) {
            const crudMessage: string = translate(`${translationPath}.${messageId}.text`) as string;
            const filesToUpload: string = completeType === CompleteType.BULK ? numberUploadFiles.toString() : "1";
            maskMessage = formatString(crudMessage, uploadedFiles.toString(), filesToUpload);
            if (messageId === "fileUploadInProgress") {
                additionalMaskMessage = translate(`${translationPath}.uploadMayTakeFewMinutes.text`) as string;
                ariaRole = uploadedFiles === 0 ? "alert" : "status";
            }
            if (messageId === "invalidBatchUploads") {
                maskClass = "df-mask-indicator df-mask-indicator-rejected";
            }
        } else if (rejectedTaskCount > 0 && reopened === 0 && inProgress === 0 && completed > 0) {
            if ((rejectedTaskCount === completed || rejectedTaskCount === completed + rejected)
            || ((rejectedTaskCount !== completed + rejected) && (completed > rejectedTaskCount - rejected))) {
                /*
                Partial rejection:
                rejectedTaskCount !== completed + rejected -> not all of the tasks were rejected after the Collection phase (status 'completed') or in the Collection phase ('rejected')
                If some tasks were not rejected later on, there'll be more of the tasks with status 'completed'
                */
                ariaRole = MaskMessage.PARTIAL_REJECTION;
                if (rejectedTaskCount === completed || rejectedTaskCount === completed + rejected) {
                    /*
                    All the tasks are rejected with no chance of re-editing them (terminal rejection) - in two cases:
                    1. all the tasks are rejected in other phases (rejectedTaskCount === completed)
                    2. some (or all) of the tasks were rejected in the Collection phase, and the others were rejected in other phases (rejectedTaskCount === completed + rejected)
                     */
                    ariaRole = MaskMessage.TERMINAL_REJECTION;
                }
            }
        } else if (intakeCompleted) {
            ariaRole = MaskMessage.INTAKE_COMPLETED;
        } else if (reopened >= 0 && inProgress > 0) {
            maskMessage = "";
        }

        return {
            maskMessage,
            maskClass,
            additionalMaskMessage,
            ariaRole: ariaRole || "alert",
            taskStatusCount,
        };
    }

    /**
     * Skip link for instructions page
     *
     * @return {void}
     */
    focusInstructionsHeader(): void {
        this.instructionsContent.current?.focus();
    }

    /**
     * Skip link for main manifold page
     *
     * @return {void}
     */
    focusManifoldContent(): void {
        this.manifoldContent.current?.focus();
    }

    /**
     * Skip link for dictation page
     *
     * @return {void}
     */
    focusDictationContent(): void {
        this.manifoldContent.current?.focus();
    }

    createPageContent(alert: React.ReactNode): React.ReactNode {
        const {contentType, displayType}: Props = this.props;
        const isTasksContentType: boolean = contentType === ContentType.TASKS;
        let pageContent: React.ReactNode;
        let focusMainContent: Function;

        switch (displayType) {
            case DisplayType.DICTATION:
                if (isTasksContentType) {
                    pageContent = <Dictation headerReference={this.dictationContent}/>;
                    focusMainContent = this.focusDictationContent;
                } else {
                    pageContent = <Instructions headerReference={this.instructionsContent}/>;
                    focusMainContent = this.focusInstructionsHeader;
                }
                break;
            case DisplayType.INTAKER_WIZARD:
            default:
                if (isTasksContentType) {
                    pageContent = <Tasks headerReference={this.manifoldContent} alert={alert}/>;
                    focusMainContent = this.focusManifoldContent;
                } else {
                    pageContent = <Instructions headerReference={this.instructionsContent}/>;
                    focusMainContent = this.focusInstructionsHeader;
                }
                break;
        }

        return (
            <CommonView focusMainContent={focusMainContent}>
                {pageContent}
                <MessageHolder/>
            </CommonView>
        );
    }

    createSpinIndicator(additionalMaskMessage: string): SpinIndicator {
        const {jobMaskData: {displaySpinner}}: Props = this.props;
        let indicator: SpinIndicator = (<></>);
        if (displaySpinner) {
            const spinnerContent: React.ReactNode[] = [];
            spinnerContent.push(<LoadingOutlined key="spinner-icon-key"/>);
            if (additionalMaskMessage) {
                spinnerContent.push(
                    <span
                        className="df-additional-mask-message"
                        key="spinner-message-key"
                    >
                        {additionalMaskMessage}
                    </span>,
                );
            }
            indicator = (
                <span>
                    {spinnerContent}
                </span>
            );
        }
        return indicator;
    }

    /**
     * Creates mask tooltip and alert for rejection/completion info
     * @param {MaskData} maskData
     * @returns React.ReactNode[]
     */
    createMaskTipAndAlert(maskData: MaskData): React.ReactNode[] {
        const {maskMessage, ariaRole}: MaskData = maskData;
        const {translationPath}: typeof MainPage = MainPage;
        const {rejectedTaskCount}: Props = this.props;

        let tip: React.ReactNode;
        let alert: React.ReactNode;

        switch (ariaRole) {
            case MaskMessage.INTAKE_COMPLETED:
            case MaskMessage.TERMINAL_REJECTION:
            case MaskMessage.PARTIAL_REJECTION:
                tip = (
                    <MaskMessageHolder
                        ariaRole={ariaRole}
                        translationPath={translationPath}
                        rejectedTaskCount={rejectedTaskCount}
                    />
                );
                alert = (
                    <MaskMessageHolder
                        ariaRole={ariaRole}
                        translationPath={translationPath}
                        rejectedTaskCount={rejectedTaskCount}
                    />
                );
                break;
            default:
                tip = (
                    <span role={ariaRole}>
                        {maskMessage}
                    </span>
                );
                break;
        }

        return [tip, alert];
    }

    /**
     * Submits all tasks after phase expiry
     */
    submitTasksAfterExpiry(): void {
        this.props.completeIntake({
            event: COMPLETE_EVENT,
            taskList: [],
            completeType: CompleteType.BULK,
            isRetry: false,
            isSubmitAfterExpiry: true,
        });
    }

    /**
     * Clears interval id, updates phase with expired access status
     */
    markPhaseAsExpired(): void {
        const {intervalId}: State = this.state;
        this.props.setPhaseDetails({accessStatus: PhaseAccessStatus.EXPIRED});

        if (intervalId) {
            clearInterval(intervalId);
            this.setState({intervalId: null});
        }
    }

    /**
     * Displays a expiration time dialog for job.
     */
    showExpiredJobDialog(): void {
        const dialogTranslatePath: string = `${MainPage.translationPath}.ExpiredJobDialog`;
        const {translate, allowedSubmission}: Props = this.props;
        this.setState({isExpiredDialogShowed: true});
        const modalType: ModalFunc = allowedSubmission ? Modal.confirm : Modal.warning;

        modalType({
            icon: <CloseCircleOutlined/>,
            className: "df-expired-time-dialog",
            centered: true,
            title: translate(`${dialogTranslatePath}.title.text`),
            content: translate(
                `${dialogTranslatePath}.${allowedSubmission ? "allowedSubmissionContent" : "expiredContent"}.text`,
            ),
            okText: translate(`${dialogTranslatePath}.${allowedSubmission ? "submitBtn" : "closeBtn"}.text`),
            okButtonProps: !allowedSubmission ? {type: "default"} : {},
            cancelText: allowedSubmission
                ? translate(`${dialogTranslatePath}.cancelBtn.text`)
                : translate(`${dialogTranslatePath}.closeBtn.text`),
            onOk: allowedSubmission ? this.submitTasksAfterExpiry.bind(this) : undefined,
        });
    }

    render(): React.ReactNode {
        const {
            jobMaskData: {visible, messageId, completeType},
            statusStats,
            rejectedTaskCount,
            translate,
            editor,
            numberOfTasks,
            validBatchUploads,
        }: Props = this.props;
        const {translationPath}: typeof MainPage = MainPage;
        const {statusClass}: StatusData = calculateStatusData({
            statusStats,
            rejectedTaskCount,
            translate,
            translationPath,
            numberOfTasks,
        });

        const isMaskVisible: boolean = visible
            || statusClass === "df-job-not-ready"
            || !validBatchUploads;
        const message: string = validBatchUploads ? messageId : "invalidBatchUploads";
        const taskStatusCount: TaskStatusCount = calculateStatusCount(statusStats);
        const maskData: MaskData = this.createMaskData({
            messageId: message,
            statusClass,
            taskStatusCount,
            completeType,
        });
        const {maskClass, additionalMaskMessage}: MaskData = maskData;
        const [tooltip, alert]: React.ReactNode[] = this.createMaskTipAndAlert(maskData);
        return (
            <Spin
                spinning={isMaskVisible}
                indicator={this.createSpinIndicator(additionalMaskMessage)}
                size="large"
                tip={tooltip}
                className={maskClass}
            >
                {editor && this.createPageContent(alert)}
            </Spin>
        );
    }
}

function mapStateToProps({
    layout: {content: {contentType}},
    project,
    schema: {parsedSchemas},
    job: {
        jobMaskData,
        jobPhase,
        uploadedFiles,
        tasks,
    },
    localize,
    uploadFiles: {
        selectedFiles,
    },
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: EditorType = parsedSchemas[schemaKey] as EditorType;
    const translate: TranslateFunction = getTranslate(localize);
    const numberUploadFiles: number = getUploadFileList(selectedFiles).length;
    const validBatchUploads: boolean = editor
        ? !!editor.batchUploads && !!editor.uploadReference
            && batchUploadsAreValid(editor.batchUploads, editor.uploadReference)
        : true;
    const {
        timeLimit,
        expireTimestamp,
        allowedSubmission,
        status: phaseStatus,
        accessStatus,
    }: JobPhase = jobPhase;

    return {
        contentType,
        displayType: editor?.layout || DisplayType.INTAKER_WIZARD,
        jobMaskData,
        translate,
        statusStats: jobPhase?.phaseStats,
        numberUploadFiles,
        rejectedTaskCount: jobPhase?.taskStats.REJECTED || 0,
        uploadedFiles,
        editor,
        numberOfTasks: tasks.length,
        validBatchUploads,
        accessStatus,
        timeLimit,
        expireTimestamp,
        allowedSubmission,
        phaseStatus,
    };
}

const mapDispatchToProps: DispatchProps = {
    setPhaseDetails,
    completeIntake,
};

export default connect(mapStateToProps, mapDispatchToProps)(MainPage);
